import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Package, Download } from "lucide-react";
import { api, rupee, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_COLOR = {
  New: "bg-blue-100 text-blue-700", Confirmed: "bg-indigo-100 text-indigo-700",
  Packed: "bg-amber-100 text-amber-700", Shipped: "bg-purple-100 text-purple-700",
  "Out for Delivery": "bg-cyan-100 text-cyan-700", Delivered: "bg-emerald-100 text-emerald-700",
  Cancelled: "bg-rose-100 text-rose-700", Returned: "bg-gray-100 text-gray-700",
};

export default function Account() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
  }, [user, nav]);

  const downloadInvoice = async (orderId, orderNumber) => {
    try {
      const response = await fetch(`${API}/orders/${orderId}/invoice`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("bc_token")}` }
      });
      if (!response.ok) {
        if (response.status === 404) {
          toast.error("Invoice not yet generated");
        } else {
          throw new Error("Download failed");
        }
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice-${orderNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download invoice");
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl font-light">Hi, {user.name}</h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
        <button onClick={() => { logout(); nav("/"); }} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium hover:border-primary" data-testid="logout-btn">
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>

      <h2 className="font-semibold text-lg mb-4 flex items-center gap-2"><Package className="w-5 h-5 text-primary" /> Order History</h2>
      {orders.length === 0 ? (
        <p className="text-muted-foreground py-10 text-center">No orders yet.</p>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} className="bg-white rounded-2xl border border-border p-5" data-testid={`order-${o.order_number}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-mono font-semibold">{o.order_number}</p>
                  <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · {o.payment_method.toUpperCase()}</p>
                </div>
                <span className={`text-xs font-semibold rounded-full px-3 py-1 ${STATUS_COLOR[o.status] || "bg-secondary"}`}>{o.status}</span>
              </div>
              <div className="mt-3 space-y-1">
                {o.items.map((it, i) => (
                  <p key={i} className="text-sm text-muted-foreground">{it.name} <span className="text-xs">({[it.size, it.color].filter(Boolean).join("/")})</span> × {it.qty}</p>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-border space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">{o.payment_note}</span>
                  <span className="font-mono font-bold">{rupee(o.total)}</span>
                </div>
                {o.invoice_path && (
                  <button 
                    onClick={() => downloadInvoice(o.id, o.order_number)}
                    className="inline-flex items-center gap-2 text-sm text-primary font-medium hover:underline"
                    data-testid={`download-invoice-${o.order_number}`}
                  >
                    <Download className="w-4 h-4" /> Download Invoice
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
